/**
 * Installing The Lab on a phone (spec/lab-world.md §12). Chrome and Edge offer the
 * install through `beforeinstallprompt`; Safari on iOS never fires it, so there the
 * offer is the words for its Share menu. Once the page runs standalone there is nothing
 * to offer. The offer is shown once on the welcome card and lives in Settings; the page
 * never nags.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

import { uiSprite } from './ui-sprite.js';

export type InstallAvailability = 'installed' | 'prompt' | 'manual' | 'none';

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export const INSTALL_HINT_IOS = 'In Safari, tap Share, then "Add to Home Screen".';
export const INSTALL_HINT_ANDROID =
  'In Chrome, open the menu (⋮), tap "Add to Home screen", then "Install".';
export const INSTALL_HINT_OTHER =
  'In your browser menu, choose "Install app" or "Add to Home Screen".';

/** True when the page already runs as an installed app. */
export function isStandalone(win: Window = window): boolean {
  try {
    const nav = win.navigator as Navigator & { standalone?: boolean };
    if (nav.standalone === true) return true;
    return (
      win.matchMedia?.('(display-mode: standalone)').matches === true ||
      win.matchMedia?.('(display-mode: fullscreen)').matches === true
    );
  } catch {
    return false;
  }
}

/** iPhones and iPads (an iPad reports itself as a Mac with a touch screen). */
export function isIOS(win: Window = window): boolean {
  try {
    const { userAgent, platform, maxTouchPoints } = win.navigator;
    if (/iPad|iPhone|iPod/.test(userAgent)) return true;
    return platform === 'MacIntel' && maxTouchPoints > 1;
  } catch {
    return false;
  }
}

/** An Android phone or tablet. */
export function isAndroid(win: Window = window): boolean {
  try {
    return /Android/.test(win.navigator.userAgent);
  } catch {
    return false;
  }
}

/** A phone or tablet: a coarse pointer, or a mobile browser saying so. */
export function isMobile(win: Window = window): boolean {
  try {
    if (win.matchMedia?.('(pointer: coarse)').matches) return true;
    return /Mobi|Android|iPhone|iPad/.test(win.navigator.userAgent);
  } catch {
    return false;
  }
}

export class InstallOffer {
  private readonly _win: Window;
  private _deferred: BeforeInstallPromptEvent | null = null;
  private _installed = false;
  private readonly _listeners = new Set<() => void>();

  constructor(win: Window = window) {
    this._win = win;
  }

  /** Start catching the browser's offer. Call before anything awaits, the event comes early. */
  listen(): this {
    this._win.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this._deferred = e as BeforeInstallPromptEvent;
      console.info('[TheLab] the browser offers to install');
      this._notify();
    });
    this._win.addEventListener('appinstalled', () => {
      this._installed = true;
      this._deferred = null;
      this._notify();
    });
    return this;
  }

  get availability(): InstallAvailability {
    if (this._installed || isStandalone(this._win)) return 'installed';
    if (this._deferred) return 'prompt';
    if (isMobile(this._win)) return 'manual';
    return 'none';
  }

  /** What to say when the browser gives no prompt of its own. */
  get hint(): string {
    if (isIOS(this._win)) return INSTALL_HINT_IOS;
    return isAndroid(this._win) ? INSTALL_HINT_ANDROID : INSTALL_HINT_OTHER;
  }

  /** Ask the browser to install; the visitor answers its dialog. */
  async prompt(): Promise<InstallOutcome> {
    const ev = this._deferred;
    if (!ev) return 'unavailable';
    this._deferred = null;
    try {
      await ev.prompt();
      const { outcome } = await ev.userChoice;
      if (outcome === 'accepted') this._installed = true;
      this._notify();
      return outcome;
    } catch {
      this._notify();
      return 'unavailable';
    }
  }

  onChange(cb: () => void): () => void {
    this._listeners.add(cb);
    return () => this._listeners.delete(cb);
  }

  private _notify(): void {
    for (const cb of this._listeners) cb();
  }
}

export interface InstallOfferViewOptions {
  doc?: Document;
}

/**
 * The offer as a button with a line under it. Hidden (not absent) while there is nothing
 * to offer, since the browser's prompt can arrive after the page is up.
 */
export function renderInstallOffer(
  offer: InstallOffer,
  opts: InstallOfferViewOptions = {},
): HTMLElement {
  const doc = opts.doc ?? document;
  const el = doc.createElement('div');
  el.className = 'install-offer';
  el.innerHTML = `
    <button type="button" data-action="install"></button>
    <p class="install-hint" hidden></p>`;
  const button = el.querySelector('[data-action="install"]') as HTMLButtonElement;
  button.append(
    uiSprite('ui-install', { doc, size: 22 }),
    doc.createTextNode('Install The Lab on this device'),
  );
  const hint = el.querySelector('.install-hint') as HTMLElement;
  const say = (text: string): void => {
    hint.textContent = text;
    hint.hidden = false;
  };
  const refresh = (): void => {
    const a = offer.availability;
    el.hidden = a === 'none' || a === 'installed';
  };
  button.addEventListener('click', async () => {
    switch (offer.availability) {
      case 'prompt': {
        const outcome = await offer.prompt();
        if (outcome === 'accepted') {
          say('Installed. Open it from your home screen.');
          button.hidden = true;
          el.hidden = false;
        } else if (outcome === 'dismissed') say('Not installed. The offer stays in Settings.');
        else say(offer.hint);
        break;
      }
      case 'manual':
        say(offer.hint);
        break;
      default:
        break;
    }
  });
  offer.onChange(() => {
    if (button.hidden) return;
    refresh();
  });
  refresh();
  return el;
}

/**
 * One line saying what this page sees, for Settings — so a visitor who cannot install can
 * say what the page saw: where it runs, whether the browser offered, and the app shell.
 */
export async function installReport(offer: InstallOffer, win: Window = window): Promise<string> {
  const where = isStandalone(win) ? 'Running as the installed app' : 'Running in the browser';
  const a = offer.availability;
  const prompt =
    a === 'prompt'
      ? "the browser's offer is here"
      : a === 'installed'
        ? 'installed'
        : 'no offer from the browser yet';
  let shell = 'no app shell';
  try {
    const sw = win.navigator.serviceWorker;
    if (!sw) shell = 'app shell unsupported';
    else {
      const reg = await sw.getRegistration();
      shell = reg?.active
        ? 'app shell ready'
        : reg
          ? 'app shell installing'
          : 'app shell not registered';
    }
  } catch {
    shell = 'app shell unknown';
  }
  return `${where} · ${prompt} · ${shell}`;
}

/** The app shell's service worker, for the install and for opening while offline. */
export async function registerServiceWorker(
  win: Window = window,
  path = '/sw.js',
): Promise<boolean> {
  try {
    if (!('serviceWorker' in win.navigator)) return false;
    // the script itself is never taken from the HTTP cache: a new deploy is seen on the next visit
    await win.navigator.serviceWorker.register(path, { updateViaCache: 'none' });
    return true;
  } catch (err) {
    console.warn('[TheLab] service worker not registered:', err);
    return false;
  }
}
